/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from '@solana/kit';
import { getKeyDecoder, getKeyEncoder, type Key, type KeyArgs } from '../types';

export type StakePool = {
  key: Key;
  stakeMint: Address;
  rewardMint: Address;
  poolId: bigint;
  stakeVault: Address;
  rewardVault: Address;
  totalStaked: bigint;
  totalRewardsOwed: bigint;
  rewardRate: bigint;
  minStakeAmount: bigint;
  lockupPeriod: bigint;
  isPaused: boolean;
  enforceLockup: boolean;
  bump: number;
  poolEndDate: Option<bigint>;
  pendingRewardRate: Option<bigint>;
  rewardRateChangeTimestamp: Option<bigint>;
  lastRateChange: Option<bigint>;
  reserved: ReadonlyUint8Array;
};

export type StakePoolArgs = {
  key: KeyArgs;
  stakeMint: Address;
  rewardMint: Address;
  poolId: number | bigint;
  stakeVault: Address;
  rewardVault: Address;
  totalStaked: number | bigint;
  totalRewardsOwed: number | bigint;
  rewardRate: number | bigint;
  minStakeAmount: number | bigint;
  lockupPeriod: number | bigint;
  isPaused: boolean;
  enforceLockup: boolean;
  bump: number;
  poolEndDate: OptionOrNullable<number | bigint>;
  pendingRewardRate: OptionOrNullable<number | bigint>;
  rewardRateChangeTimestamp: OptionOrNullable<number | bigint>;
  lastRateChange: OptionOrNullable<number | bigint>;
  reserved: ReadonlyUint8Array;
};

export function getStakePoolEncoder(): Encoder<StakePoolArgs> {
  return getStructEncoder([
    ['key', getKeyEncoder()],
    ['stakeMint', getAddressEncoder()],
    ['rewardMint', getAddressEncoder()],
    ['poolId', getU64Encoder()],
    ['stakeVault', getAddressEncoder()],
    ['rewardVault', getAddressEncoder()],
    ['totalStaked', getU64Encoder()],
    ['totalRewardsOwed', getU64Encoder()],
    ['rewardRate', getU64Encoder()],
    ['minStakeAmount', getU64Encoder()],
    ['lockupPeriod', getI64Encoder()],
    ['isPaused', getBooleanEncoder()],
    ['enforceLockup', getBooleanEncoder()],
    ['bump', getU8Encoder()],
    ['poolEndDate', getOptionEncoder(getI64Encoder())],
    ['pendingRewardRate', getOptionEncoder(getU64Encoder())],
    ['rewardRateChangeTimestamp', getOptionEncoder(getI64Encoder())],
    ['lastRateChange', getOptionEncoder(getI64Encoder())],
    ['reserved', fixEncoderSize(getBytesEncoder(), 7)],
  ]);
}

export function getStakePoolDecoder(): Decoder<StakePool> {
  return getStructDecoder([
    ['key', getKeyDecoder()],
    ['stakeMint', getAddressDecoder()],
    ['rewardMint', getAddressDecoder()],
    ['poolId', getU64Decoder()],
    ['stakeVault', getAddressDecoder()],
    ['rewardVault', getAddressDecoder()],
    ['totalStaked', getU64Decoder()],
    ['totalRewardsOwed', getU64Decoder()],
    ['rewardRate', getU64Decoder()],
    ['minStakeAmount', getU64Decoder()],
    ['lockupPeriod', getI64Decoder()],
    ['isPaused', getBooleanDecoder()],
    ['enforceLockup', getBooleanDecoder()],
    ['bump', getU8Decoder()],
    ['poolEndDate', getOptionDecoder(getI64Decoder())],
    ['pendingRewardRate', getOptionDecoder(getU64Decoder())],
    ['rewardRateChangeTimestamp', getOptionDecoder(getI64Decoder())],
    ['lastRateChange', getOptionDecoder(getI64Decoder())],
    ['reserved', fixDecoderSize(getBytesDecoder(), 7)],
  ]);
}

export function getStakePoolCodec(): Codec<StakePoolArgs, StakePool> {
  return combineCodec(getStakePoolEncoder(), getStakePoolDecoder());
}

export function decodeStakePool<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>
): Account<StakePool, TAddress>;
export function decodeStakePool<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>
): MaybeAccount<StakePool, TAddress>;
export function decodeStakePool<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>
): Account<StakePool, TAddress> | MaybeAccount<StakePool, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getStakePoolDecoder()
  );
}

export async function fetchStakePool<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig
): Promise<Account<StakePool, TAddress>> {
  const maybeAccount = await fetchMaybeStakePool(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeStakePool<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig
): Promise<MaybeAccount<StakePool, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeStakePool(maybeAccount);
}

export async function fetchAllStakePool(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig
): Promise<Account<StakePool>[]> {
  const maybeAccounts = await fetchAllMaybeStakePool(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeStakePool(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig
): Promise<MaybeAccount<StakePool>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeStakePool(maybeAccount));
}
